/**
 * @fileoverview PublicRoute Component
 * @description Route wrapper for public pages (redirects authenticated users)
 * @module @xtream/frontend/router/PublicRoute
 *
 * Features:
 * - Redirects authenticated users away from login page
 * - Supports redirect to intended destination after login
 *
 * Updated in FASE 14 for proper auth store integration
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@store/auth.store';
import { ROUTES } from './routes';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface PublicRouteProps {
  /** Child components to render */
  children: React.ReactNode;
  /** Whether to redirect authenticated users (e.g., login page) */
  redirectAuthenticated?: boolean;
}

interface LocationState {
  from?: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * PublicRoute - Wrapper for public pages
 * Optionally redirects authenticated users away from pages like login
 *
 * @example
 * ```tsx
 * <Route
 *   path="/login"
 *   element={
 *     <PublicRoute redirectAuthenticated>
 *       <LoginPage />
 *     </PublicRoute>
 *   }
 * />
 * ```
 */
export const PublicRoute: React.FC<PublicRouteProps> = ({
  children,
  redirectAuthenticated = false,
}) => {
  const location = useLocation();
  const state = location.state as LocationState | null;

  // Get auth state from Zustand store
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);

  // While loading, show nothing or a minimal loader
  if (isLoading) {
    return null;
  }

  // If authenticated and this route should redirect authenticated users
  if (isAuthenticated && redirectAuthenticated) {
    // Redirect to the page they were trying to access, or dashboard
    const redirectTo = state?.from || ROUTES.DASHBOARD;
    return <Navigate to={redirectTo} replace />;
  }

  // Render children
  return <>{children}</>;
};

export default PublicRoute;
